/**
 * Champ d autocomplete pour selectionner un dossier (client x mandataire) dans
 * l inbox. Utilise searchDossiersAutocomplete pour le fetch debounced.
 *
 * Le composant est non-controlle : on emit l item complet sur selection
 * (onSelect), pas juste l id.
 *
 * @param {{
 *   onSelect: (item: { dossierId: string, clientName: string, mandataireCode: string }) => void,
 *   placeholder?: string,
 * }} props
 */

import { useState, useEffect, useRef } from 'react';
import { searchDossiersAutocomplete } from '../../api/ceeInbox.js';
import { useDebouncedCallback } from '../lib/useDebouncedCallback.js';

export default function ClientAutocomplete({ onSelect, placeholder = 'Rechercher un client...' }) {
  const [query, setQuery] = useState('');
  const [items, setItems] = useState([]);
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const wrapperRef = useRef(null);

  // Click outside ferme la liste
  useEffect(() => {
    const handler = (e) => {
      if (!wrapperRef.current?.contains(e.target)) setOpen(false);
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  const search = useDebouncedCallback(async (q) => {
    if (!q || q.length < 2) {
      setItems([]);
      setLoading(false);
      return;
    }
    try {
      const results = await searchDossiersAutocomplete(q);
      setItems(Array.isArray(results) ? results : []);
    } catch (_err) {
      setItems([]);
    } finally {
      setLoading(false);
    }
  }, 300);

  const onChange = (e) => {
    const v = e.target.value;
    setQuery(v);
    setOpen(true);
    setLoading(true);
    search(v);
  };

  const pick = (item) => {
    setQuery(`${item.clientName} (${item.mandataireCode})`);
    setOpen(false);
    onSelect(item);
  };

  return (
    <div ref={wrapperRef} className="relative w-full">
      <input
        type="text"
        value={query}
        onChange={onChange}
        onFocus={() => query && setOpen(true)}
        placeholder={placeholder}
        className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm bg-white focus:ring-2 focus:ring-blue-500 focus:border-blue-500 focus:outline-none"
      />
      {open && (items.length > 0 || loading) && (
        <ul className="absolute z-10 mt-1 w-full bg-white border border-gray-100 rounded-lg shadow-lg max-h-60 overflow-y-auto">
          {loading && <li className="px-3 py-2 text-xs text-[#94a3b8]">Recherche...</li>}
          {!loading && items.map((it) => (
            <li
              key={it.dossierId}
              onClick={() => pick(it)}
              className="px-3 py-2 text-sm hover:bg-blue-50 cursor-pointer flex items-center justify-between"
            >
              <span className="truncate">{it.clientName}</span>
              <span className={`text-[10px] uppercase font-bold px-1.5 py-0.5 rounded ml-2 ${
                it.mandataireCode === 'FEH' ? 'bg-blue-100 text-blue-700' : 'bg-orange-100 text-orange-700'
              }`}>
                {it.mandataireCode}
              </span>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
